import { getIndexerValueForMailboxInfo, type MailboxInfo } from 'owa-client-types';
import isCopilotFeatureEnabled from './isCopilotFeatureEnabled';
import { getApplicationSettings } from 'owa-application-settings';
import { logUsage } from 'owa-analytics';
import { trace } from 'owa-trace';
import { getItem, type LocalStorageKeys, setItem } from 'owa-local-storage';
import { isCopilotStoreReady } from 'owa-copilot-settings-store';
import type { CopilotEnablementDiagnostics } from 'owa-copilot-settings-store/lib/utils/enablementCodes/CopilotEnablementTypes';
import { MChatEnablementCodes } from 'owa-copilot-settings-store/lib/utils/enablementCodes/MChatEnablementCodes';
import { getLinkedLicensingMailboxInfoOrSelf } from 'owa-session-store';

/**
 * @param mailboxInfo Mailbox info
 * @returns true if MChat is enabled, false if the feature is disabled, undefined if the feature is in progress or not started
 * based on the feature flags and OCS license check
 */
export default function isMChatEnabled(mailboxInfo: MailboxInfo): boolean | undefined {
    const hasCopilotBusinessChatLicense = isMChatFeatureEnabled(mailboxInfo);
    /* eslint-disable-next-line owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
     * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
     *	> Invalid Outlook telemetry EventName "hasCopilotBusinessChatLicense": must start with a capital letter (A–Z). */
    logUsage('hasCopilotBusinessChatLicense', mailboxInfo, { hasCopilotBusinessChatLicense });
    trace.info(
        'License Check Feature enabled and HasCopilotBusinessChatLicense: ' +
            hasCopilotBusinessChatLicense
    );
    return hasCopilotBusinessChatLicense;
}

/**
 * @param mailboxInfo Mailbox info
 * @param skipLanguageCheck  whether to skip language check. Default is true.
 * @param shouldIgnoreUserPreferences whether to ignore user preferences
 * @param diagnostics Optional object to receive diagnostic code when disabled
 * @returns true if the MChat Copilot feature is enabled through OCS, false if the feature is disabled, undefined if the feature is in progress or not started
 */
export function isMChatFeatureEnabled(
    mailboxInfoToQuery: MailboxInfo,
    skipLanguageCheck?: boolean,
    shouldIgnoreUserPreferences?: boolean,
    diagnostics?: CopilotEnablementDiagnostics
): boolean | undefined {
    const mailboxInfo = getLinkedLicensingMailboxInfoOrSelf(mailboxInfoToQuery);
    const copilotStoreReady = isCopilotStoreReady(mailboxInfo);
    const indexer = getIndexerValueForMailboxInfo(mailboxInfo);
    const key: LocalStorageKeys = `isMChatFeatureEnabled${indexer}`;

    if (!copilotStoreReady) {
        const cachedMChatFeatureEnabled = getItem(window, key);
        const cachedMChatFeatureResult = cachedMChatFeatureEnabled
            ? cachedMChatFeatureEnabled === 'true'
            : undefined;

        if (diagnostics && !cachedMChatFeatureResult) {
            diagnostics.code =
                cachedMChatFeatureResult === false
                    ? MChatEnablementCodes.MCHAT_CACHED_DISABLED
                    : MChatEnablementCodes.MCHAT_STORE_NOT_READY;
        }

        return cachedMChatFeatureResult;
    }

    const enableMultiLanguage =
        skipLanguageCheck ?? getApplicationSettings('Copilot', mailboxInfo).enableMultiLanguage;

    const ocsMChatFeatureEnabled = isCopilotFeatureEnabled(
        'MChat',
        mailboxInfo,
        enableMultiLanguage,
        shouldIgnoreUserPreferences,
        diagnostics
    );

    setItem(window, key, (ocsMChatFeatureEnabled ?? false).toString());

    return ocsMChatFeatureEnabled;
}
